import { useEffect, useState } from "react";
import { Storage } from "aws-amplify";
import { Image } from "./types";

export const useImageSelection = (images: Image[]) => {
    const [selected, setSelected] = useState<{ [key: string]: boolean }>({});

    useEffect(() => {
        const initialSelected = Object.fromEntries(
            images.map(image => [image.canopyS3Key, image.enable || false])
        );
        setSelected(initialSelected);
    }, [images]);

    const onChangeRow = (id: string) => (value: boolean) => {
        setSelected(prev => ({ ...prev, [id]: value }));
    };

    const handleSelectAll = () => {
        const allSelected = Object.fromEntries(images.map(image => [image.canopyS3Key, true]));
        setSelected(allSelected);
    };

    const handleClearSelection = () => {
        const allCleared = Object.fromEntries(images.map(image => [image.canopyS3Key, false]));
        setSelected(allCleared);
    };

    return { selected, onChangeRow, handleSelectAll, handleClearSelection };
};

export const useImageUrls = (images: Image[]) => {
    const [imageUrls, setImageUrls] = useState<{ [key: string]: string }>({});

    useEffect(() => {
        const loadImages = async () => {
            const urls: { [key: string]: string } = {};
            for (const image of images) {
                try {
                    const presignedUrl = await Storage.get(image.canopyS3Key, { expires: 3600 });
                    const response = await fetch(presignedUrl);
                    const blob = await response.blob();
                    urls[image.canopyS3Key] = URL.createObjectURL(blob);
                } catch (error) {
                    console.error(`Error loading image ${image.canopyS3Key}:`, error);
                }
            }
            setImageUrls(urls);
        };

        loadImages();

        return () => {
            Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [images]);

    return imageUrls;
};

export const useImageExpansion = () => {
    const [expandedImage, setExpandedImage] = useState<string | null>(null);
    const [isImageLoading, setIsImageLoading] = useState(false);
    const [zoomLevel, setZoomLevel] = useState(1);

    const handleExpandImage = (imageUrl: string) => {
        setIsImageLoading(true);
        setExpandedImage(imageUrl);
        setZoomLevel(1);
    };

    const handleCloseExpanded = () => {
        setExpandedImage(null);
        setZoomLevel(1);
    };

    const handleZoom = (direction: "in" | "out" | "reset") => {
        switch (direction) {
            case "in":
                setZoomLevel(prev => Math.min(prev + 0.1, 3));
                break;
            case "out":
                setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
                break;
            case "reset":
                setZoomLevel(1);
                break;
        }
    };

    return {
        expandedImage,
        isImageLoading,
        zoomLevel,
        handleExpandImage,
        handleCloseExpanded,
        handleZoom,
        setIsImageLoading,
    };
};

export const useUpdateAppeal = (
    updateAppeal: any,
    data: any,
    alias: string,
    onSuccess: () => void
) => {
    return async (selected: { [key: string]: boolean }) => {
        try {
            const updatedData = JSON.parse(JSON.stringify(data));

            if (
                updatedData.appealDetails?.appealApproachCalculationData.exportAppealsPackage
                    .mapsAndImages
            ) {
                updatedData.appealDetails.appealApproachCalculationData.exportAppealsPackage.mapsAndImages =
                    updatedData.appealDetails.appealApproachCalculationData.exportAppealsPackage.mapsAndImages.map(
                        (image: Image) => ({
                            ...image,
                            enable: selected[image.canopyS3Key] || false,
                        })
                    );
            }

            await updateAppeal({
                businessUseCase: "SMART",
                appealArtifactId: data.appealArtifactId,
                appealDetails: updatedData.appealDetails,
                appealStatus: data.appealDetails.status,
                clientName: alias,
            });

            onSuccess();
        } catch (error) {
            console.error("Error updating appeal:", error);
        }
    };
};
